import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { FaceDetector, FilesetResolver } from '@mediapipe/tasks-vision';
import { AuthService } from './auth.service';
import { AssessmentService } from './assessment.service';
import { ActivityService } from './activity.service';

@Injectable({
	providedIn: 'root'
})
export class StreamActivityService {
	private recordedBlobs: Blob[] = [];
	private queueRecordedBlobs: Blob[] = [];
	private chunkIndex = 0;

	isRecording = false;
	selectedCamera: string | null = null;
	selectedMicrophone: string | null = null;

	faceDetector!: FaceDetector;
	faceDetectionInterval: any;
	detect_face = false;
	light = false;
	frameRate = 0;

	totalSlices = 0;
	uploadedSlices = 0;
	remainingSlices = 0;
	videoLength = 0;

	project_id!: number;
	case_id!: number;
	activity_id!: number;
	baseline: any = {
		baseline_start: null,
		baseline_end: null,
		base_voice_start: null,
		base_voice_end: null
	};

	private _passConditions$ = new BehaviorSubject<boolean>(false);
	uploadProgressObs: Subject<{ progress: number, finishUploading: boolean}> = new Subject<{ progress: number, finishUploading: boolean}>();

	constructor(private assessmentService: AssessmentService, private activityService: ActivityService, private authService: AuthService) {}

	async storeChunk(chunk: Blob) {
		this.recordedBlobs.push(chunk);
		this.queueRecordedBlobs.push(chunk);
	}

	async stopStreaming() {
		clearInterval(this.faceDetectionInterval);
	}

	async updateActivityStatus(project_id: number, case_id: number, activity_id: number, speakerSelected: string | null) {
		await this.activityService.updateActivityStatus(project_id, case_id, activity_id, { activity_type: 'conversation' }, { status: 'STARTED', single_speaker: speakerSelected, baseline: this.baseline });
	}

	async processUploadQueue() {
		if (this.queueRecordedBlobs.length > 0) {
			this.totalSlices = this.recordedBlobs.length;

			const slice = this.queueRecordedBlobs.shift();

			if (slice) {
				const storage_route = `project_${this.project_id}/case_${this.case_id}/chunks/conversation-${this.activity_id}`;
				const signedUrl = await this.getSignedUrl(`${storage_route}/chunk_${this.chunkIndex.toString().padStart(4, '0')}.webm`, slice.type);

				try {					
					await this.uploadChunk(slice, signedUrl);
					this.uploadedSlices++;
					this.chunkIndex++;
	
					this.uploadProgressObs.next({ progress: this.getUploadProgress(), finishUploading: false });
				} catch (error) {
					this.queueRecordedBlobs.unshift(slice);
				}
			}

			this.processUploadQueue();
		} else {
			if (!this.isRecording) {
				this.uploadProgressObs.next({ progress: 100, finishUploading: true });
				const d = { frameRate: this.frameRate, baseline: this.baseline, videoLength: this.videoLength };
				this.assessmentService.mergeChunks(this.project_id, this.case_id, this.activity_id, { activity_type: 'conversation' }, d).subscribe({
					next: () => {
						return;
					},
					error: () => {
						return;
					}
				});
			} else {
				setTimeout(() => { this.processUploadQueue(); }, 5000);
			}
		}
	}

	getUploadProgress(): number {
		if (this.totalSlices === 0) return 0; // Avoid division by zero
		return (this.uploadedSlices / this.totalSlices) * 100;
	}

	async getSignedUrl(file_name: string, file_type: string) {
		const maxRetries = 6; // Maximum number of retries
		let retryCount = 0;
		const retryDelay = 10000; // 10 seconds in milliseconds
	
		while (retryCount < maxRetries) {
			try {
				const signedUrl = await this.activityService.getSignedUrl(file_name, file_type);
				return signedUrl
			} catch (error) {
				console.error('Upload error:', error);
			}
	
			retryCount++;
	
			if (retryCount < maxRetries) {
				console.warn(`Retrying upload attempt ${retryCount} in 10 seconds...`);
				await new Promise(resolve => setTimeout(resolve, retryDelay));
			}
		}
	
		this.authService.toastErrorMessage('There was a connection failure!', 'Connection Failed');
		// TODO what will do if there was a connection error while recording!
		console.error('Upload failed after maximum retries.');
	}

	async uploadChunk(slice: Blob, signedUrl: string) {
		const maxRetries = 7; // Maximum number of retries
		let retryCount = 0;
		const retryDelay = 5000; // 5 seconds in milliseconds
	
		while (retryCount < maxRetries) {
			try {
				const response = await fetch(signedUrl, {
					method: 'PUT',
					headers: {
						'Content-Type': slice.type,
					},
					body: slice,
				});
	
				if (response.ok) {
					return; 
				} else {
					console.error('Upload error:', response.statusText);
				}
			} catch (error) {
				console.error('Upload error:', error);
			}
	
			retryCount++;
	
			if (retryCount < maxRetries) {
				console.warn(`Retrying upload attempt ${retryCount} in 5 seconds...`);
				await new Promise(resolve => setTimeout(resolve, retryDelay));
			}
		}
	
		this.authService.toastErrorMessage('There was a connection failure!', 'Connection Failed');
		// TODO what will do if there was a connection error while recording!
		console.error('Upload failed after maximum retries.');
	}

	get passConditions$() { return this._passConditions$.asObservable(); }

	async checkConditions(videoPlayer: HTMLVideoElement) {
		const vision = await FilesetResolver.forVisionTasks("https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision/wasm");
		this.faceDetector = await FaceDetector.createFromOptions(
			vision,
			{
				baseOptions: {
					modelAssetPath: "https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite"
				},
				runningMode: "VIDEO",
				minDetectionConfidence: 0.7
			}
		);

		if (this.faceDetectionInterval) {
			clearInterval(this.faceDetectionInterval);
		}

		this.faceDetectionInterval = setInterval(() => {
			if (this.faceDetector) {
				const results = this.faceDetector.detectForVideo(videoPlayer, Date.now());
				if (results.detections.length === 1) {
					this.detect_face = true;
				} else {
					this.detect_face = false;
				}

				this.light = this.updateLightQuality(videoPlayer);
				this._passConditions$.next(this.detect_face && this.light)
			}
		}, 1000);
	}

	getLuminance(imageData: ImageData): number {
		let totalLuminance = 0;
		const data = imageData.data;

		for (let i = 0; i < data.length; i += 4) {
				const r = data[i];
				const g = data[i + 1];
				const b = data[i + 2];
				totalLuminance += 0.2126 * r + 0.7152 * g + 0.0722 * b;
		}

		return totalLuminance / (imageData.width * imageData.height);
	}

	updateLightQuality(videoPlayer: HTMLVideoElement) {
		const videoFrame = videoPlayer;
		if (videoFrame && videoFrame.videoWidth > 0 && videoFrame.videoHeight > 0) { // Check if video is loaded
			const canvas = document.createElement('canvas');
			canvas.width = videoFrame.videoWidth;
			canvas.height = videoFrame.videoHeight;
	
			const context = canvas.getContext('2d');
	
			if (context) { // Check if context is not null
				context.drawImage(videoFrame, 0, 0);
	
				const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
				const luminance = this.getLuminance(imageData);
	
				const minLuminance = 103; // Adjust based on your requirements
				const maxLuminance = 200; // Adjust based on your requirements
	
				return (luminance >= minLuminance && luminance <= maxLuminance);
			} else {
				console.error('2D context is not available.');
			}
		} else {
			setTimeout(() => {
				this.updateLightQuality(videoPlayer);
			}, 1000);
		}
		return false;
	}	
}